import { ApiError, apiFetch, authHeaders } from './client';

/**
 * File attachment helpers. Separate from `apiFetch` because:
 *   - uploads are multipart/form-data, not JSON;
 *   - callers want progress events, which `fetch` doesn't expose.
 *
 * Downloads use a plain `<a href>` to `GET /api/documents/...` so
 * browsers can handle PDF preview + "Save As" without us piping the
 * bytes through JS.
 */

export type EntityType =
  | 'Compra'
  | 'Pago'
  | 'Instrumento'
  | 'Retencion'
  | 'Recepcion'
  | 'FacturaVenta'
  | 'FacturaVentaAnticipada'
  | 'NotaCreditoVenta'
  | 'ComprobanteVenta'
  | 'ComprobanteInterCo'
  | 'ComprobanteTesoreria';

export type UploadDocumentResult = {
  driveItemId: string;
  fileName: string;
  size: number;
};

export type UploadProgress = {
  loaded: number;
  total: number;
};

/**
 * Upload a `File` or `Blob` to the server. Returns the drive item id +
 * canonical filename (which may include the timestamp prefix the server
 * adds). `onProgress` fires on XHR upload progress events.
 */
export function uploadDocument(args: {
  entityType: EntityType;
  entityId: string;
  file: File | Blob;
  /** Filename to send when `file` is a `Blob` (e.g., a jsPDF-generated certificate). */
  fileName?: string;
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
}): Promise<UploadDocumentResult> {
  const { entityType, entityId, file, fileName, onProgress, signal } = args;
  const form = new FormData();
  form.set('entityType', entityType);
  form.set('entityId', entityId);
  // Blob has no `.name`; File does. Send the explicit filename when
  // provided (PDF generators pass their preferred name).
  if (fileName) form.set('file', file, fileName);
  else form.set('file', file);

  return new Promise<UploadDocumentResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/documents/upload');
    // XHR bypasses `apiFetch`, so we have to attach BOTH the bearer
    // token AND the X-Active-Country header by hand. Forgetting the
    // country header makes the backend fall back to the user's primary
    // pais and 404 on rows from the other country (2026-05-11: surfaced
    // when uploading a comprobante on an MX venta — the server scoped
    // sales_payment_receipts to AR and returned 404 even though the
    // row existed in MX scope).
    for (const [k, v] of Object.entries(authHeaders())) {
      xhr.setRequestHeader(k, v);
    }
    xhr.responseType = 'json';

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress({ loaded: e.loaded, total: e.total });
      });
    }

    const abort = () => xhr.abort();
    signal?.addEventListener('abort', abort);

    xhr.onload = () => {
      signal?.removeEventListener('abort', abort);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response as UploadDocumentResult);
      } else {
        const payload = (xhr.response ?? {}) as {
          error?: string;
          message?: string;
          details?: unknown;
        };
        reject(
          new ApiError(
            xhr.status || 0,
            payload.error ?? 'upload_failed',
            payload.message,
            // Carry through `details` so callers can read structured
            // payloads like the `stock_insuficiente` shortage list.
            payload.details,
          ),
        );
      }
    };
    xhr.onerror = () => {
      signal?.removeEventListener('abort', abort);
      reject(new ApiError(0, 'network_error', 'Fallo de red al subir el archivo.'));
    };
    xhr.onabort = () => {
      signal?.removeEventListener('abort', abort);
      reject(new ApiError(0, 'aborted', 'Subida cancelada.'));
    };

    xhr.send(form);
  });
}

// ---------------------------------------------------------------------------
// Invoice extraction (Gemini)
// ---------------------------------------------------------------------------

export type ExtractedInvoiceFields = {
  nroComprobante: string | null;
  fechaComprobante: string | null;
  cuit: string | null;
  proveedorNombre: string | null;
  total: number | null;
  tipoComprobante:
    | 'Factura A'
    | 'Factura B'
    | 'Factura C'
    | 'Nota de Débito'
    | 'Nota de Crédito'
    | 'Recibo'
    | 'Otros Comprobantes'
    | null;
  moneda: 'Pesos Argentinos' | 'Dolares' | null;
  vencimientoPago: string | null;
};

export type InvoiceExtractionResult = {
  source: 'gemini';
  model: string;
  fields: ExtractedInvoiceFields;
  hasAny: boolean;
};

/**
 * Discriminated outcome of a single extract-invoice call. Lets callers
 * branch on the failure mode instead of conflating "no api key", "rate
 * limited", and "real network failure" into a single null.
 *
 * - `ok` — extraction returned (may still have all-null fields if the
 *   AI couldn't read the document; check `result.hasAny`).
 * - `quota` — Gemini's free-tier RESOURCE_EXHAUSTED. Until the quota
 *   resets, callers should surface a warning so the user knows
 *   autocomplete is offline.
 * - `unavailable` — server returned 503 (no API key) or 502 (Gemini
 *   blocked the request / unparsable response). Silent fallback.
 * - `network` — request never made it (timeout, offline, parse error
 *   on the response). Silent fallback.
 */
export type ExtractInvoiceOutcome =
  | { kind: 'ok'; result: InvoiceExtractionResult }
  | { kind: 'quota'; message: string }
  | { kind: 'unavailable' }
  | { kind: 'network' };

/**
 * Send a factura PDF (or image) to the backend and get back the
 * extracted fields for autocomplete. Returns a tagged outcome so the
 * caller can distinguish the free-tier quota case ("warn the user")
 * from a generic failure ("silently let them type it").
 */
export async function extractInvoice(args: {
  file: File | Blob;
  fileName?: string;
  mode?: 'compra' | 'facturacion';
  signal?: AbortSignal;
}): Promise<ExtractInvoiceOutcome> {
  const { file, fileName, mode = 'compra', signal } = args;
  const form = new FormData();
  if (fileName) form.set('file', file, fileName);
  else form.set('file', file);
  form.set('mode', mode);

  let res: Response;
  try {
    res = await fetch('/api/documents/extract-invoice', {
      method: 'POST',
      body: form,
      headers: authHeaders(),
      signal,
    });
  } catch {
    return { kind: 'network' };
  }

  if (res.status === 429) {
    let message =
      'Se agotó la cuota gratuita de análisis con IA por hoy. Cargá los datos a mano o esperá a que se renueve.';
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      // Use the default message.
    }
    return { kind: 'quota', message };
  }
  if (res.status === 503 || res.status === 502 || res.status === 400) {
    return { kind: 'unavailable' };
  }
  if (!res.ok) {
    // eslint-disable-next-line no-console
    console.warn('[documents] extractInvoice unexpected status', res.status);
    return { kind: 'network' };
  }
  try {
    const result = (await res.json()) as InvoiceExtractionResult;
    return { kind: 'ok', result };
  } catch {
    return { kind: 'network' };
  }
}

/**
 * URL that streams the stored file. Use in `<a href>` / `window.open`
 * to let the browser handle PDF preview. Auth is picked up from the
 * Authorization header on fetch-based clients, but for direct link
 * navigation the browser won't send it — for those flows use
 * `openDocumentInNewTab()` which opens a blob URL instead.
 *
 * When `driveItemId` is provided the URL targets the per-file route
 * (`/file/:driveItemId`) — used for multi-file entities like Recepción
 * where many files live in the same folder.
 */
export function documentDownloadUrl(
  entityType: EntityType,
  entityId: string,
  driveItemId?: string,
): string {
  return driveItemId
    ? `/api/documents/${entityType}/${entityId}/file/${driveItemId}`
    : `/api/documents/${entityType}/${entityId}`;
}

export type EntityFile = {
  driveItemId: string;
  fileName: string;
  size: number;
  webUrl?: string;
  /** ISO-8601 from Graph; usually present, but treat as optional. */
  lastModified?: string;
};

/**
 * List every file currently in the entity's drive folder. Only
 * meaningful for entities flagged `multiFile` server-side (Recepción
 * today). Returns `[]` when the folder doesn't exist yet.
 */
export async function listEntityFiles(
  entityType: EntityType,
  entityId: string,
): Promise<EntityFile[]> {
  const res = await apiFetch<{ files: EntityFile[] }>(
    `/api/documents/${entityType}/${entityId}/files`,
  );
  return res.files;
}

export type FetchedDocument = {
  /** Blob URL (caller is responsible for revoking when done). */
  url: string;
  /** Content-Type from the server response, or 'application/octet-stream'. */
  mimeType: string;
};

/**
 * Fetch the file with auth and return a blob URL + mime type. Caller owns
 * the URL lifecycle — call `URL.revokeObjectURL(url)` when the preview is
 * dismissed.
 */
export async function fetchDocumentBlobUrl(
  entityType: EntityType,
  entityId: string,
  driveItemId?: string,
): Promise<FetchedDocument> {
  const res = await fetch(documentDownloadUrl(entityType, entityId, driveItemId), {
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'download_failed', `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  return {
    url: URL.createObjectURL(blob),
    mimeType: res.headers.get('content-type') ?? 'application/octet-stream',
  };
}

/**
 * Fetch the file, turn it into a blob URL, and open that in a new tab.
 * Needed because anchor navigations don't carry our bearer token — we
 * have to fetch + reconstitute.
 */
export async function openDocumentInNewTab(
  entityType: EntityType,
  entityId: string,
  driveItemId?: string,
): Promise<void> {
  const { url } = await fetchDocumentBlobUrl(entityType, entityId, driveItemId);
  // Don't revoke immediately — the new tab still needs to load it. The
  // browser will GC the blob URL when the tab is closed.
  const opened = window.open(url, '_blank');
  if (!opened) {
    // Popup blocked. Fall back to same-tab navigation.
    window.location.href = url;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Fetch the file with auth and trigger a "Save As" via a synthetic
 * anchor click. Same fetch-then-blob pattern as `openDocumentInNewTab`,
 * but the anchor's `download` attribute makes the browser save instead
 * of inline-render. The blob URL is revoked once the click is dispatched.
 */
export async function downloadDocument(
  entityType: EntityType,
  entityId: string,
  fileName: string,
  driveItemId?: string,
): Promise<void> {
  const { url } = await fetchDocumentBlobUrl(entityType, entityId, driveItemId);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Browsers need a tick to start the download before we revoke. The
  // fixed delay matches `openDocumentInNewTab`.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export type DeleteDocumentResponse =
  | { outcome: 'deleted' }
  | { outcome: 'already_absent' };

export async function deleteDocument(
  entityType: EntityType,
  entityId: string,
  driveItemId?: string,
): Promise<DeleteDocumentResponse> {
  // Per-file delete (multi-file entities) routes through `/file/:driveItemId`;
  // single-file delete clears the row's reference + drive item via the
  // bare entity URL. The two paths return the same shape.
  const url = driveItemId
    ? `/api/documents/${entityType}/${entityId}/file/${driveItemId}`
    : `/api/documents/${entityType}/${entityId}`;
  return apiFetch<DeleteDocumentResponse>(url, { method: 'DELETE' });
}
